import { AppLinks } from 'app/router/consts/app-links.enum';
import { createPoint, WorkingHoursPicker } from 'features/index';
import { IWeekWorkTime } from 'features/working-hours-picker/interfaces/time-table-input.interface';
import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ICreatePointDto } from 'shared/api/business/interfaces/create-point-dto.interface';
import { DEFAULT_TIME_TABLE } from 'shared/consts/schedule';
import { Alert, DefaultButton, FormButtonBack, FormInput } from 'shared/ui';
import { validateCreatePointForm } from '../model/validate-create-point-form';
import styles from './create-point-form.module.scss';
import { userStore } from 'entities/index';
import { observer } from 'mobx-react';
import { useAppMediaQuery } from 'shared/hooks';

interface ICreatePointFormProps {
  businessId: string;
}

export const CreatePointForm: React.FC<ICreatePointFormProps> = observer(
  ({ businessId }) => {
    const isTablet = useAppMediaQuery('tablet');
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState(userStore.phone);
    const [weekWorkTime, setWeekWorkTime] = useState<IWeekWorkTime | null>(
      DEFAULT_TIME_TABLE
    );
    const [isEmployeeMode, setIsEmployeeMode] = useState(false);
    const [isSwitchActive, setIsSwitchActive] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const handleCloseAlert = useCallback(() => setAlertMessage(null), []);
    const [validationError, setValidationError] = useState<boolean | string>(
      false
    );
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isOnboarding = searchParams.get('onboarding') === 'true';

    const handleSubmit = async () => {
      if (isEmployeeMode && !isSwitchActive) {
        setValidationError(
          'выберите другой режим графика работы точки "каждый день" или "неделя"'
        );
        return;
      }
      const errors = validateCreatePointForm({ name, address, phone });
      if (!errors) {
        try {
          if (!businessId) {
            setValidationError('ID бизнеса не найден');
            return;
          }
          const data: ICreatePointDto = {
            name,
            address,
            contactPhone: phone,
            postIndex: null,
            weekWorkTime: isEmployeeMode ? null : weekWorkTime,
            businessId,
          };
          const pointId = await createPoint(data);
          if (!pointId) {
            setValidationError('Ошибка: не удалось получить ID точки');
            return;
          }
          setAlertMessage('Точка успешно создана');
          setTimeout(() => {
            if (isOnboarding) {
              const path =
                AppLinks.CreateEmployeeLink.replace(':id', pointId) +
                `?onboarding=true&businessId=${businessId}`;
              navigate(path);
            } else {
              const path = AppLinks.BusinessLink.replace(':id', businessId);
              navigate(path);
            }
          }, 1500);
        } catch (error) {
          if (error instanceof Error) {
            setValidationError(error.message);
          }
        }
      } else {
        setValidationError(errors);
      }
    };

    return (
      <>
        <div className={styles.create_point}>
          <div className={styles.create_point__content}>
            <div className={styles.create_point__title}>Регистрация точки</div>
            <div className={styles.create_point__inputs}>
              <div className={styles.inputs__input}>
                <FormInput
                  inputTitle="Название точки"
                  inputPlaceholder="Название"
                  onChange={(value) => {
                    setName(value);
                  }}
                />
              </div>
              <div className={styles.inputs__input}>
                <FormInput
                  inputTitle="Адрес"
                  inputPlaceholder="Адрес точки"
                  onChange={(value) => {
                    setAddress(value);
                  }}
                />
              </div>
              <div className={styles.inputs__input}>
                <FormInput
                  isPhone={true}
                  inputValue={phone}
                  inputTitle="Номер телефона для связи"
                  onChange={(value) => {
                    setPhone(value.match(/\d+/g)?.join('') ?? '');
                  }}
                />
              </div>
              <div className={styles.inputs__input}>
                <WorkingHoursPicker
                  inputTitle="График работы"
                  onChangeTimeTableHandler={(value) => {
                    setWeekWorkTime(value);
                    setIsEmployeeMode(value === null);
                  }}
                  inputTimeTable={isEmployeeMode ? null : weekWorkTime}
                  showEmployeeOption={true}
                  isWorkTimeByEmployee={isEmployeeMode}
                  onSwitchChange={(active) => setIsSwitchActive(active)}
                />
              </div>
            </div>
            <div className={styles.create_point__buttons}>
              <div className={styles.buttons__error}>{validationError}</div>
              <div
                className={styles.buttons__button_submit}
                onClick={handleSubmit}
              >
                <DefaultButton
                  buttonText="Зарегистрировать точку"
                  color="white"
                  style={
                    isTablet
                      ? {
                          fontSize: '18px',
                        }
                      : undefined
                  }
                />
              </div>
              <div className={styles.buttons__button_back}>
                <FormButtonBack />
              </div>
            </div>
          </div>
        </div>
        {alertMessage && (
          <Alert
            type="success"
            message={alertMessage}
            onClose={handleCloseAlert}
          />
        )}
      </>
    );
  }
);
